import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";

import { Subscription } from "rxjs";

import {
    ITablePrice,
    IPriceLine,
    TPriceLine,
    IPriceValue,
    TPriceColumn,
    IPriceLineDetail,
    PriceLineType,
    PriceColumnType,
    TPriceValues,
    IAirLine,
    ITotalLine,
    IOtherLine,
    IHotelLine
} from "../../ITablePrice";
import {
    ISectorAir,
    ISectorStay,
    ITraveller
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { ILabels } from 'libraries/interfaces/ILabels';
import { TOTAL_KEY } from '../../mappers/keys-mapper.service';
import { Yii } from 'libraries/interfaces/Yii';
import { TravellerDataTranslationService } from 'libraries/service/traveller-data-translation/traveller-data-translation.service';
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import {
    IMultiselectConfig,
    IMultiselectOptions
} from "libraries/module/formbuilder/fields/multiselect/multiselect.model";

const HEADER_COLUMN: string = 'lines';

@Component({
    selector: 'price-tab',
    templateUrl: './price-tab.component.html',
    styleUrls: ['./price-tab.component.scss'],
})
export class PriceTabComponent implements OnChanges {
    @Input()
    public prices: ITablePrice;

    @Input()
    public travellers: ITraveller[];

    public filtersFieldsConfig: {
        traveller: IFieldSpecs,
    };

    public filtersOptions: {
        traveller: IMultiselectOptions[],
    };

    public filtersConfig: {
        traveller: IMultiselectConfig,
    };

    public displayedColumns: string[] = [];
    public dataSource: { values: ITableData[]; columns: TPriceColumn[] };
    public labels: ILabels = {
        noPrice: Yii.t('wui-shared-js', 'Aucune information de tarification à afficher'),
    };

    public parentKeySelected: string[] = [];
    public filterFormGroup: FormGroup;
    public isFilterDisabled: boolean = false;
    public hasSectorDetailsShown: boolean = false;
    private subscription: Subscription;

    constructor(
        public readonly travellerDataTranslationService: TravellerDataTranslationService,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.prices) {
            this.dataSource = transformData(this.prices);
            this.displayedColumns = this.dataSource.columns
                .map((column: TPriceColumn) => column.key);
        } else {
            this.dataSource = null;
            this.displayedColumns = [];
        }

        if (changes && changes.travellers && changes.travellers.currentValue) {
            this.initFilters();
            this.isFilterDisabled = changes.travellers.currentValue.length < 2;
        }

    }

    public toggleDetails(key: string): void {
        if (!this.detailsIsOpen(key)) {
            this.parentKeySelected.push(key);
        } else {
            this.parentKeySelected = this.parentKeySelected.filter((k: string) => k !== key);
        }
    }

    public detailsIsOpen(key: string): boolean {
        return this.parentKeySelected.some((k: string) => k === key);
    }

    public isString(value: unknown): boolean {
        return typeof value === 'string' || value instanceof String;
    }

    private initFilters(): void {
        this.filtersOptions = getFiltersOptions(this.travellers);
        this.filterFormGroup = initFiltersFormGroup(this.formBuilder, this.filtersOptions);
        this.filtersFieldsConfig = {
            traveller: {
                formGroup: this.filterFormGroup,
                formControlName: "traveller",
            },
        };
        this.filtersConfig = {
            traveller: {
                defaultLabel: Yii.t('wui-shared-js', 'Filtrer sur les voyageurs'),
                displaySelectedLabel: false,
            },
        };

        this.subscription = this.filterFormGroup.valueChanges
            .subscribe({
                next: (filterValue: { traveller: string[] }): void => {
                    if (this.dataSource) {
                        this.displayedColumns = this.dataSource.columns
                            .slice(0, 2)
                            .map((column: TPriceColumn) => column.key)
                            .concat(filterValue.traveller);
                    }
                }
            });
    }
}

function transformData(data: ITablePrice): { values: ITableData[]; columns: TPriceColumn[] } {
    const columns: TPriceColumn[] = [
        {
            key: HEADER_COLUMN,
            type: PriceColumnType.detail,
            text: ''
        },
        ...data.columns
    ];

    const linesValues: ITableData[] = getLineValues(data.lines, data.values, columns);

    return {
        columns,
        values: linesValues
    };
}

function getLineValues(lines: TPriceLine[], values: TPriceValues, columns: TPriceColumn[]): ITableData[] {
    return lines
        .map((line: TPriceLine) => {
            let lineValues: ILines = null;

            switch (line.type) {
                case PriceLineType.air:
                    lineValues = {
                        type: line.type,
                        value: (line as IAirLine).sectors,
                        data: line,
                        isImportant: true
                    };
                    break;
                case PriceLineType.stay:
                    lineValues = {
                        type: line.type,
                        value: (line as IHotelLine).hotel,
                        data: line,
                        isImportant: true
                    };
                    break;
                case PriceLineType.total:
                    lineValues = {
                        type: line.type,
                        value: (line as ITotalLine).text,
                        data: line,
                        isImportant: true,
                        additionalInfos: (line as ITotalLine).additionalInfos,
                    };
                    break;
                default:
                    const textLine: IPriceLineDetail | IOtherLine = line;
                    lineValues = {
                        type: line.type,
                        value: textLine.text,
                        data: textLine,
                    };
            }

            return {
                lines: lineValues,
                ...getValues(line, values, columns),
            };
        });
}

function getValues(line: IPriceLine, values: TPriceValues, columns: TPriceColumn[]): {
    [travellerXId: string]: IPriceValue;
    total?: IPriceValue;
} {
    const lineValues: Map<string, IPriceValue> = values.get(line.key);

    const columnKey: string[] = columns
        .filter((column: TPriceColumn) => column.key !== HEADER_COLUMN)
        .map((column: TPriceColumn) => column.key);

    const constructedValues: { total: IPriceValue } = {
        total: lineValues && lineValues.get(TOTAL_KEY) ? lineValues.get(TOTAL_KEY) : {
            value: '-',
            isAutomaticallyDistributed: false
        } as IPriceValue
    };

    columnKey.forEach((key: string) => {
        constructedValues[key] = lineValues
            && lineValues.get(key)
            && lineValues.get(key).value !== undefined
            ? lineValues.get(key)
            : { value: '-' };
    });

    return constructedValues;
}

interface ITableData {
    [key: string]: IPriceValue | ILines | ITableData[];
}

interface ILines {
    value: string | ISectorAir[] | ISectorStay;
    type: PriceLineType;
    data?: TPriceLine;
    isImportant?: boolean;
    additionalInfos?: string[];
}

function getFiltersOptions(travellers: ITraveller[]): { traveller: IMultiselectOptions[] } {
    const traveller: IMultiselectOptions[] = (travellers || [])
        .map((travellerItem: ITraveller) => ({
            label: `${travellerItem.firstName} ${travellerItem.lastName}`,
            value: travellerItem.xId
        }));
    return {
        traveller,
    };
}

function initFiltersFormGroup(fb: FormBuilder, options: { traveller: IMultiselectOptions[] }): FormGroup {
    return fb.group({
        traveller: fb.control(options.traveller.map((travellerOption: IMultiselectOptions) => travellerOption.value)),
    });
}